<template>
  <div class="w-full flex flex-col min-h-screen">
    <header
      class="bg-white dark:bg-slate-900 border-b border-slate-50 dark:border-slate-800"
    >
      <div class="flex flex-row justify-between items-center py-2">
        <div class="flex w-full py-2 px-4">
          <h1
            class="m-0 text-lg text-slate-900 dark:text-slate-100 whitespace-nowrap mt-1 my-0"
          >
            {{ $t('BROADCAST.FORM.BROADCAST_FORM') }}
          </h1>
        </div>
        <woot-button class="clear my-2 mx-2" type="submit" @click="goBack">
          {{ $t('BROADCAST.FORM.BACK_LABEL') }}
        </woot-button>
      </div>
    </header>
    <section class="conversation-page bg-white dark:bg-slate-900 overflow-auto">
      <div class="px-4 py-2">
        <div class="gap-2 flex flex-row">
          <div class="w-[50%] mr-2">
            <label class="mb-2">{{ $t('BROADCAST.TABLE.INBOX') }}</label>
            <multiselect
              v-model="inboxSelected"
              :max-height="250"
              :options="inboxesListOption"
              track-by="value"
              label="label"
              :multiple="false"
              :close-on-select="true"
              :clear-on-select="true"
              :placeholder="$t('BROADCAST.FORM.CHOOSE_INBOX')"
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
              class="custom-multiselect"
              @select="onInboxSelected"
            />
          </div>

          <div class="w-[50%]">
            <label class="mb-2">{{
              $t('BROADCAST.FORM.CUSTOMER_LABEL')
            }}</label>
            <div class="flex flex-row items-center">
              <span
                class="text mb-0 bg-gray-100 dark:bg-slate-800 py-3 px-2 mr-2 w-full"
              >
                {{ selectedCustomerText }}
              </span>
              <woot-button
                class="clear"
                icon="add"
                color-scheme="success"
                data-testid="create-new-broadcast"
                @click="showCustomerList"
              >
              </woot-button>
            </div>
          </div>
        </div>

        <div v-if="inboxSelected" class="gap-2 flex flex-row">
          <div class="w-[50%] mr-2">
            <label class="mb-2">{{
              $t('BROADCAST.FORM.TEMPLATE_LABEL')
            }}</label>
            <multiselect
              v-model="selectedTemplateValue"
              :max-height="250"
              :options="templateListOption"
              track-by="value"
              label="label"
              :multiple="false"
              :close-on-select="true"
              :clear-on-select="true"
              :placeholder="$t('BROADCAST.FORM.CHOOSE_TEMPLATE')"
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
              class="custom-multiselect"
              @select="onTemplateChange"
            />
            <div v-if="checkTemplateHeader">
              <div class="flex flex-col items-center mb-2">
                <label class="self-start">{{
                  $t('BROADCAST.FORM.ADD_MEDIA')
                }}</label>
                <input
                  type="file"
                  @change="onFileChange"
                  accept="image/*"
                  class="my-3"
                />
              </div>
            </div>
            <div v-if="selectedTemplate" class="w-full">
              <div v-if="variables" class="template__variables-container">
                <div
                  v-for="(variable, key) in processedParams"
                  :key="key"
                  class="template__variable-item"
                >
                  <label class="mb-2">
                    <span class="text-red-500 mr-1">*</span>Parameter {{ key }}
                  </label>

                  <div class="flex flex-row items-center mb-2">
                    <input
                      v-if="
                        ![
                          'date_picker',
                          'month_picker',
                          'time_picker',
                        ].includes(parameterTypes[key])
                      "
                      v-model="processedParams[key]"
                      type="text"
                      class="variable-input w-full mr-2 mb-0"
                      :disabled="
                        ['customer_name', 'total_bill'].includes(
                          parameterTypes[key]
                        )
                      "
                    />
                    <input
                      v-else-if="parameterTypes[key] === 'date_picker'"
                      v-model="processedParams[key]"
                      type="date"
                      class="variable-input w-full mr-2 border px-2 mb-0"
                    />
                    <input
                      v-else-if="parameterTypes[key] === 'month_picker'"
                      v-model="processedParams[key]"
                      type="month"
                      class="variable-input w-full mr-2 border px-2 mb-0"
                    />
                    <input
                      v-else-if="parameterTypes[key] === 'time_picker'"
                      v-model="processedParams[key]"
                      type="time"
                      class="variable-input w-full mr-2 border px-2 mb-0"
                    />
                    <woot-button
                      class="clear"
                      icon="add"
                      color-scheme="secondary"
                      data-testid="create-new-broadcast"
                      @click="openVariableTypeSelector(key)"
                    >
                    </woot-button>
                  </div>
                </div>
                <p v-if="$v.$dirty && $v.$invalid" class="error">
                  {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
                </p>
              </div>
            </div>
            <div v-if="checkTemplateButton">
              <label class="mb-2">{{ $t('BROADCAST.FORM.URL_BUTTON') }}</label>

              <div class="flex flex-row items-center">
                <input
                  v-model="templateButtonUrl"
                  type="text"
                  class="variable-input mb-0 w-full"
                  placeholder="example.com"
                  :disabled="['bill_url'].includes(templateButtonUrl)"
                />

                <woot-button
                  class="clear"
                  icon="add"
                  color-scheme="secondary"
                  data-testid="create-new-broadcast"
                  @click="openButtonTypeModal()"
                >
                </woot-button>
              </div>
            </div>
          </div>
          <woot-modal
            :show.sync="showTypeModal"
            :on-close="closeVariableTypeModal"
          >
            <div class="h-auto overflow-auto flex flex-col mb-30 pb-10">
              <woot-modal-header
                :header-title="$t('BROADCAST.FORM.CHOOSE_VARIABLE')"
              />
              <div class="mt-2 px-8">
                <div v-for="option in paramsTypeOptions" :key="option.value">
                  <button
                    @click="selectVariableType(option.value)"
                    class="list-group-item wo w-full d-flex justify-content-between px-2 py-4 click-list bg-white dark:bg-slate-900"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </div>
            </div>
          </woot-modal>

          <woot-modal
            :show.sync="showButtonTypeModal"
            :on-close="closeButtonTypeModal"
          >
            <div class="h-auto overflow-auto flex flex-col mb-30 pb-10">
              <woot-modal-header
                :header-title="$t('BROADCAST.FORM.CHOOSE_VARIABLE')"
              />
              <div class="mt-2 px-8">
                <div v-for="option in buttonTypeOptions" :key="option.value">
                  <button
                    @click="selectButtonType(option.value)"
                    class="list-group-item wo w-full d-flex justify-content-between px-2 py-4 click-list bg-white dark:bg-slate-900"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </div>
            </div>
          </woot-modal>
          <div class="w-[50%]">
            <div v-if="selectedTemplate" class="flex flex-col mr-6">
              <div class="whatsapp-container">
                <div class="whatsapp-message">
                  <div class="message-content">
                    <div
                      v-if="checkTemplateHeader"
                      class="mb-4 flex items-center align-center"
                    >
                      <img
                        :src="templateHeader"
                        alt="Header Image"
                        class="w-full max-w-xs shadow"
                      />
                    </div>
                    <pre
                      class="info-text whitespace-pre-line"
                      v-html="previewText"
                    ></pre>
                    <div v-if="checkTemplateButton">
                      <div class="button-container">
                        <button
                          v-for="(btn, index) in templateButton"
                          :key="index"
                          class="payment-button"
                        >
                          {{ btn.text }}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="message-tail"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="mx-4 py-2">
        <woot-button variant="smooth" @click="sendBroadcast">
          {{ $t('BROADCAST.SEND_BROADCAST') }}
        </woot-button>
      </div>
    </section>
    <woot-modal
      :show.sync="showCustomerModal"
      :on-close="closeModal"
      size="medium"
    >
      <div class="h-auto overflow-auto flex flex-col mb-30 pb-10">
        <woot-modal-header :header-title="$t('BROADCAST.FORM.CUSTOMER_LIST')" />
        <div class="mt-4 px-8">
          <div class="broadcast-table overflow-auto">
            <div class="flex flex-row">
              <div class="w-[50%]">
                <multiselect
                  v-model="selectedPackages"
                  :max-height="250"
                  :options="packageListOption"
                  track-by="value"
                  label="label"
                  :multiple="true"
                  :clear-on-select="true"
                  :hide-selected="true"
                  :placeholder="$t('BROADCAST.FORM.CHOOSE_PACKAGE')"
                  :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
                  :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
                  class="custom-multiselect"
                  @select="onPackageChange"
                  @remove="onRemovePackage"
                />
              </div>
              <div class="w-[50%] ml-2">
                <multiselect
                  v-model="selectedHdr"
                  :max-height="250"
                  :options="hdrListOption"
                  track-by="value"
                  label="label"
                  :multiple="true"
                  :close-on-select="true"
                  :clear-on-select="true"
                  :hide-selected="true"
                  :placeholder="$t('BROADCAST.FORM.CHOOSE_HDR')"
                  :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
                  :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
                  class="custom-multiselect"
                  @select="onHdrChange"
                  @remove="onRemoveHdr"
                />
              </div>
            </div>
            <div class="flex flex-row">
              <div class="w-[50%]">
                <multiselect
                  v-model="selectedStatus"
                  :max-height="250"
                  :options="statuses"
                  track-by="value"
                  label="label"
                  :close-on-select="true"
                  :clear-on-select="true"
                  :hide-selected="true"
                  :placeholder="$t('BROADCAST.FORM.CHOOSE_BILL_STATUS')"
                  :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
                  :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
                  class="custom-multiselect"
                  @select="onStatusChange"
                />
              </div>

              <div class="w-[50%] ml-2">
                <div
                  v-if="selectedStatus.value == 'belum_bayar'"
                  class="multiselect-wrap--medium"
                >
                  <Datepicker
                    v-model="selectedPeriod"
                    type="month"
                    :not-auto-close="true"
                    class="w-full"
                    :placeholder="$t('BROADCAST.FORM.CHOOSE_PERIOD')"
                    :input-class="'px-3 py-2 h-[46px] border rounded-md text-base border-gray-300'"
                  />
                </div>
              </div>
            </div>
            <div class="flex flex-row justify-between mb-2">
              <div
                class="w-full min-w-[100px] flex items-center relative search-wrap"
              >
                <div class="flex items-center absolute h-full left-2.5">
                  <fluent-icon
                    icon="search"
                    class="h-5 leading-9 text-sm text-slate-700 dark:text-slate-200"
                  />
                </div>
                <input
                  @change="onSearchPelanggan"
                  type="text"
                  v-model="searchQuery"
                  :placeholder="$t('BROADCAST.FORM.SEARCH_CUSTOMER')"
                  class="contact-search border-slate-100 dark:border-slate-600 pl-10 pr-5"
                />
              </div>
              <div class="flex flex-row">
                <woot-button
                  class="clear"
                  :color-scheme="selectedCustomer.length ? 'alert' : 'success'"
                  :icon="selectedCustomer.length ? 'delete' : 'checkmark'"
                  size="small"
                  data-testid="delete-checkbox"
                  @click="handleAllSelect"
                >
                  {{
                    selectedCustomer.length
                      ? $t('BROADCAST.TABLE.UNSELECT_ALL')
                      : $t('BROADCAST.TABLE.SELECT_ALL')
                  }}
                </woot-button>
                <woot-button
                  v-if="isFilterActive"
                  class="clear ml-1"
                  color-scheme="alert"
                  icon="delete"
                  size="small"
                  data-testid="delete-checkbox"
                  @click="clearFilters"
                >
                  {{ $t('BROADCAST.FORM.CLEAR_FILTER') }}
                </woot-button>
              </div>
            </div>
            <div
              v-if="uiFlags.isFetchingPelanggan"
              class="flex justify-center items-center text-center text-slate-700 dark:text-slate-200 py-4"
            >
              <spinner />{{ $t('BROADCAST.LOADING_DATA') }}
            </div>
            <div v-else>
              <ve-table
                max-height="calc(100vh - 17.500rem)"
                scroll-width="10rem"
                row-key-field-name="paket_berjalan_id"
                :columns="columns"
                :table-data="customers"
                :border-around="false"
                :checkbox-option="checkboxOption"
                :row-style-option="getRowStyleOption()"
              />
              <div v-show="showEmpty" class="empty-data">
                {{ $t('BROADCAST.FORM.NO_DATA') }}
              </div>
            </div>
          </div>
          <broadcast-footer
            :first-index="firstIndex"
            :last-index="lastIndex"
            :total-items="meta.allCount"
            :current-page="meta.currentPage"
            :per-page="meta.perPage"
            :has-prev-page="hasPrevPage"
            :has-next-page="hasNextPage"
            :items-per-page-options="[10, 25, 50, 100]"
            @update:perPage="meta.perPage = $event"
            @per-page-change="onPerPageChange"
            @first-page="onFirstPage"
            @prev-page="onPrevPage"
            @next-page="onNextPage"
            @last-page="onLastPage"
          />
          <div class="flex justify-end mt-2">
            <woot-button color-scheme="primary" @click="confirmSelection">
              {{ $t('BROADCAST.FORM.SAVE_LABEL') }}
            </woot-button>
          </div>
        </div>
      </div>
    </woot-modal>
    <woot-modal :show.sync="showConfirmModal" :on-close="closeConfirm">
      <div class="h-auto overflow-auto flex flex-col mb-30 pb-10">
        <woot-modal-header :header-title="$t('BROADCAST.CONFIRM_LABEL')" />
        <div class="mt-2 px-8">
          <p>
            {{
              $t('BROADCAST.FORM.SEND_BROADCAST_CONFIRM_MESSAGE', {
                count: selectedCustomer.length,
              })
            }}
          </p>
          <div class="mx-4 pt-4">
            <div class="w-[50%]">
              <label>
                <input
                  class="checkbox pr-1"
                  type="checkbox"
                  v-model="approveStatus"
                />
                {{ $t('BROADCAST.FORM.NEED_APPROVAL') }}
              </label>
            </div>
          </div>
          <div class="flex justify-end mt-4">
            <div
              class="flex flex-row justify-end items-center gap-2 px-0 w-full"
            >
              <woot-button @click="sendMessage" class="button clear">
                <template> {{ $t('BROADCAST.MODAL.CONFIRM_BUTTON') }}</template>
              </woot-button>
              <woot-button @click="closeConfirm" class="button clear">
                {{ $t('BROADCAST.MODAL.CANCEL_BUTTON') }}
              </woot-button>
            </div>
          </div>
        </div>
      </div>
    </woot-modal>
  </div>
</template>

<script>
const allKeysRequired = value => {
  const keys = Object.keys(value);
  return keys.every(key => value[key]);
};
import { VeTable } from 'vue-easytable';
import { requiredIf } from 'vuelidate/lib/validators';
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import BroadcastFooter from './Footer.vue';
import Spinner from 'shared/components/Spinner.vue';
import Datepicker from 'vue2-datepicker';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import Message from './Message.vue';

export default {
  mixins: [alertMixin],
  components: {
    VeTable,
    BroadcastFooter,
    Spinner,
    Datepicker,
    Message,
  },
  data() {
    return {
      selectedParamKey: null,
      templateHeaderUrl: null,
      showCustomerModal: false,
      showConfirmModal: false,
      showTypeModal: false,
      approveStatus: false,
      isPaymentTemplate: false,
      showEmpty: false,
      templateButtonUrl: '',
      searchQuery: '',
      inboxSelected: '',
      selectedTemplateValue: '',
      showButtonTypeModal: false,
      selectedTemplate: '',
      selectedStatus: '',
      selectedPeriod: '',
      uploadedImage: '',
      selectedHdr: [],
      selectedPackages: [],
      selectedCustomer: [],
      selectedRowData: [],
      processedParams: {},
      parameterTypes: {},
      checkboxOption: {
        hideSelectAll: true,
        selectedRowKeys: [],
        selectedRowChange: this.handleSelectedRowChange,
      },
      statuses: [
        { label: this.$t('BROADCAST.STATUS.ALL'), value: 'all' },
        { label: this.$t('BROADCAST.STATUS.UNPAID'), value: 'belum_bayar' },
        { label: this.$t('BROADCAST.STATUS.PAID'), value: 'sudah_bayar' },
      ],
      buttonTypeOptions: [
        { value: 'manual', label: this.$t('BROADCAST.PARAMETER.MANUAL') },
        {
          value: 'bill_url',
          label: this.$t('BROADCAST.BUTTON.BILL_URL'),
        },
      ],
      paramsTypeOptions: [
        { value: 'manual', label: this.$t('BROADCAST.PARAMETER.MANUAL'),type: 'frontend' },
        {
          value: 'customer_name',
          label: this.$t('BROADCAST.PARAMETER.CUSTOMER_NAME'),
          type: 'backend'
        },
        {
          value: 'bill_periode',
          label: this.$t('BROADCAST.PARAMETER.BILL_PERIODE'),
          type: 'backend'
        },
        { value: 'total_bill', label: this.$t('BROADCAST.PARAMETER.BILL'),type: 'backend' },
        {
          value: 'date_picker',
          label: this.$t('BROADCAST.PARAMETER.DATE_PICKER'),
          type: 'frontend'
        },
        {
          value: 'month_picker',
          label: this.$t('BROADCAST.PARAMETER.MONTH_PICKER'),
          type: 'frontend'
        },
        {
          value: 'time_picker',
          label: this.$t('BROADCAST.PARAMETER.TIME_PICKER'),
          type: 'frontend'
        },
      ],
    };
  },
  validations: {
    processedParams: {
      requiredIfKeysPresent: requiredIf('variables'),
      allKeysRequired,
    },
  },
  computed: {
    ...mapGetters({
      inboxes: 'broadcastsView/getInboxList',
      customersData: 'orders/getPelanggan',
      meta: 'orders/getPelangganMeta',
      uiFlags: 'orders/getCustomersUIFlags',
      packages: 'orders/getPaketLangganan',
      hdrs: 'hdr/getRecords',
      templates: 'broadcastsView/getInboxTemplates',
    }),
    customers() {
      if (this.customersData.length === 0) {
        this.showEmpty = true;
      } else {
        this.showEmpty = false;
      }

      return this.customersData.filter(item => item.paket_berjalan_id != null);
    },
    isFilterActive() {
      return (
        this.searchQuery !== '' ||
        this.selectedStatus !== '' ||
        this.selectedPackages.length > 0 ||
        this.selectedHdr.length > 0
      );
    },
    selectedCustomerText() {
      const customerList = this.selectedCustomer;
      if (customerList.length > 0) {
        return this.$t('BROADCAST.FORM.CHOOSEN_CUSTOMER', {
          count: customerList.length,
        });
      } else {
        return this.$t('BROADCAST.FORM.NO_CHOOSEN_CUSTOMER');
      }
    },
    packageListOption() {
      const packageOptions = [
        ...new Set(this.packages.map(item => item.nama_paket)),
      ].map(nama => ({
        label: nama,
        value: nama,
      }));

      return packageOptions;
    },
    inboxesListOption() {
      return this.inboxes.map(inbox => ({
        value: inbox.id,
        label: inbox.name,
      }));
    },
    hdrListOption() {
      return this.hdrs.map(item => ({
        label: item.name,
        value: item.id,
      }));
    },
    templateListOption() {
      return this.templates.map(template => ({
        value: template.id,
        label: template.name,
      }));
    },
    variables() {
      const variables = this.templateString.match(/{{([^}]+)}}/g);
      return variables;
    },
    checkTemplateButton() {
      return (
        this.selectedTemplate &&
        Array.isArray(this.selectedTemplate.components) &&
        this.selectedTemplate.components.some(c => c.type === 'BUTTONS')
      );
    },
    checkTemplateHeader() {
      if (
        this.selectedTemplate &&
        Array.isArray(this.selectedTemplate.components)
      ) {
        return this.selectedTemplate.components.some(
          c => c.type === 'HEADER' && c.format === 'IMAGE'
        );
      }
      return false;
    },
    templateHeader() {
      return this.templateHeaderUrl;
    },
    templateString() {
      if (this.selectedTemplate) {
        const body = this.selectedTemplate.components.find(
          c => c.type === 'BODY'
        );
        return body ? body.text : '';
      }
      return '';
    },
    templateButton() {
      if (
        this.selectedTemplate &&
        Array.isArray(this.selectedTemplate.components)
      ) {
        const buttonComponent = this.selectedTemplate.components.find(
          c => c.type === 'BUTTONS'
        );

        return buttonComponent && Array.isArray(buttonComponent.buttons)
          ? buttonComponent.buttons
          : [];
      }
      return [];
    },
    previewText() {
      return this.templateString.replace(/{{([^}]+)}}/g, (match, variable) => {
        const variableKey = this.processVariable(variable);
        const value =
          this.formatVariableValue(variableKey) || `{{${variable}}}`;
        return `<span class="font-semibold">${value}</span>`;
      });
    },
    processedString() {
      return this.templateString.replace(/{{([^}]+)}}/g, (match, variable) => {
        const variableKey = this.processVariable(variable);
        const value =
          this.formatVariableValue(variableKey) || `{{${variable}}}`;
        return value;
      });
    },
    hasPrevPage() {
      return this.meta.currentPage > 1;
    },
    hasNextPage() {
      return this.meta.currentPage < this.meta.lastPage;
    },
    firstIndex() {
      return this.meta.firstIndex;
    },
    lastIndex() {
      return this.meta.lastIndex;
    },
    columns() {
      return [
        {
          field: 'index',
          key: 'index',
          title: 'No',
          width: '30px',
          align: 'center',
          renderBodyCell: ({ rowIndex }) => {
            return (
              this.meta.perPage * (this.meta.currentPage - 1) + rowIndex + 1
            );
          },
        },
        {
          field: 'nama_pelanggan',
          key: 'nama_pelanggan',
          title: this.$t('BROADCAST.TABLE.NAME'),
          width: '150px',
          align: 'left',
          renderBodyCell: ({ row }) => {
            return (
              <div class="row-template">
                <div class="template-block">{row.nama_pelanggan}</div>
                <div class="customer-package">{row.paket}</div>
              </div>
            );
          },
        },
        {
          field: 'no_telp',
          key: 'no_telp',
          title: this.$t('BROADCAST.TABLE.PHONE_NUMBER'),
          width: '100px',
          align: 'left',
          renderBodyCell: ({ row }) => {
            return (
              <div class="row-template">
                <div class="template-block">{row.no_telp}</div>
              </div>
            );
          },
        },
        {
          field: '',
          key: 'checkbox',
          type: 'checkbox',
          title: '',
          width: '50px',
          align: 'center',
        },
      ];
    },
  },

  methods: {
    openButtonTypeModal() {
      this.showButtonTypeModal = true;
    },
    clearFilters() {
      this.searchQuery = '';
      this.selectedStatus = '';
      this.selectedPackages = [];
      this.selectedHdr = [];
      this.fetchCustomers();
    },
    async onFileChange(event) {
      const file = event.target.files[0];
      const formData = new FormData();
      formData.append('attachment', file);

      try {
        const { url } = await uploadFile(file);

        this.templateHeaderUrl = url;
        this.showAlert(this.$t('BROADCAST.SUCCESS_UPLOAD_FILE'));
      } catch (error) {
        this.showAlert(this.$t('BROADCAST.ERROR_UPLOAD_FILE'));
        console.error('Error while uploading file:', error);
      }
    },
    formatDate(dateValue) {
      if (!dateValue) return '';
      const date = new Date(dateValue);
      return new Intl.DateTimeFormat('id-ID', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(date);
    },
    formatMonth(monthValue) {
      if (!monthValue) return '';
      const [year, month] = monthValue.split('-');
      const date = new Date(year, month - 1);
      return new Intl.DateTimeFormat('id-ID', {
        month: 'long',
        year: 'numeric',
      }).format(date);
    },
    formatVariableValue(variableKey) {
      const value = this.processedParams[variableKey];

      switch (this.parameterTypes[variableKey]) {
        case 'month_picker':
          return this.formatMonth(value);
        case 'date_picker':
          return this.formatDate(value);
        default:
          return value;
      }
    },
    async handleAllSelect() {
      try {
        if (this.selectedCustomer.length) {
          this.selectedCustomer = [];
          this.selectedRowKeys = [];
          this.selectedRowData = [];
          this.checkboxOption.selectedRowKeys = [];
        } else {
          const data = await this.$store.dispatch('orders/getAllCustomerId', {
            page: this.meta.currentPage,
            per_page: 3000,
            order_by: '',
            order: '',
            search: this.searchQuery,
            filter: 'all',
            status: this.selectedStatus.value || 'all',
            select_nama_paket: this.selectedPackages.map(item => item.value),
            select_hdr: this.selectedHdr.map(item => item.value),
          });

          const all_phone_number = data.map(c => c.paket_berjalan_id);

          this.selectedRowData = data;
          this.selectedCustomer = all_phone_number;
          this.selectedRowKeys = all_phone_number;
          this.checkboxOption.selectedRowKeys = all_phone_number;
        }
      } catch (error) {
        this.showAlert(this.$t('BROADCAST.ERROR_FETCHING_CUSTOMER'));

        console.error('Error fetching customer data:', error);
      }
    },
    handleSelectedRowChange({ row, selectedRowKeys }) {
      this.selectedCustomer = selectedRowKeys;
      this.checkboxOption.selectedRowKeys = selectedRowKeys;
      const exists = this.selectedRowData.find(r => r.no_telp === row.no_telp);
      if (!exists) {
        this.selectedRowData.push(row);
      } else {
        if (!selectedRowKeys.includes(row.no_telp)) {
          this.selectedRowData = this.selectedRowData.filter(
            r => r.no_telp !== row.no_telp
          );
        }
      }
    },
    onFirstPage() {
      this.meta.currentPage = 1;
      this.fetchCustomers();
    },
    onPrevPage() {
      if (this.meta.currentPage > 1) {
        this.meta.currentPage--;
        this.fetchCustomers();
      }
    },
    onNextPage() {
      if (this.meta.currentPage < this.meta.lastPage) {
        this.meta.currentPage++;
        this.fetchCustomers();
      }
    },
    onLastPage() {
      this.meta.currentPage = this.meta.lastPage;
      this.fetchCustomers();
    },
    onPerPageChange(newPerPage) {
      this.meta.perPage = newPerPage;
      this.meta.currentPage = 1;
      this.fetchCustomers();
    },
    onStatusChange() {
      this.selectedPeriod = '';
      this.meta.currentPage = 1;
      this.fetchCustomers();
    },
    confirmSelection() {
      this.showCustomerModal = false;
    },
    formatPeriod(date) {
      if (!date) return '';
      return new Intl.DateTimeFormat('id-ID', {
        month: 'long',
        year: 'numeric',
      }).format(date);
    },
    async sendMessage() {
      this.$v.$touch();
      if (this.$v.$invalid) return;

      const formattedPeriod = this.selectedPeriod
        ? this.formatPeriod(this.selectedPeriod)
        : null;

      const hasImageHeader = this.selectedTemplate?.components?.some(
        c => c.type === 'HEADER' && c.format === 'IMAGE'
      );

      const hasButton =
        this.selectedTemplate &&
        Array.isArray(this.selectedTemplate.components) &&
        this.selectedTemplate.components.some(c => c.type === 'BUTTONS');

      let processed_params = Object.fromEntries(
        Object.entries(this.processedParams).map(([key, value]) => [
          key,
          this.formatVariableValue(key),
        ])
      );


      const payload = {
        ...(formattedPeriod && { selected_period: formattedPeriod }),
        ...(hasImageHeader && { image_url: this.templateHeaderUrl }),
        ...(hasButton && { button_url: this.templateButtonUrl }),
        is_payment: this.isPaymentTemplate,
        approval: this.approveStatus,
        data: this.selectedCustomer.map(paket_id => ({
          paket_id: paket_id,
          message: this.processedString,
          template_params: {
            name: this.selectedTemplate?.name || '',
            language: this.selectedTemplate?.language || 'id',
            category: this.selectedTemplate?.category || '',
            namespace: this.selectedTemplate?.namespace || '',
            processed_params: processed_params || {},
          },
          inbox_id: this.inboxSelected.value,
          account_id: this.$route.params.accountId,
        })),
      };

      try {
        const res = await this.$store.dispatch(
          'broadcastsView/dispatchBroadcasts',
          {
            payload,
            inboxId: this.inboxSelected.value,
          }
        );

        if (res.data.success == true) {
          this.showAlert(this.$t('BROADCAST.SUCCESS_ADD_BROADCAST_DATA'));
          this.$router.push({
            name: 'broadcasts_index',
          });
        } else {
          this.showAlert(res.data.message);
          return;
        }
      } catch (error) {
        this.showAlert(this.$t('BROADCAST.ERROR_DISPATCHING'));

        console.error('Error while dispatching broadcasts:', error);
      }
    },
    async fetchTemplates() {
      try {
        await this.$store.dispatch(
          'broadcastsView/getInboxTemplates',
          this.inboxSelected.value
        );
      } catch (error) {
        this.showAlert(this.$t('BROADCAST.ERROR_FETCHING_TEMPLATE'));

        console.error('Error while fetching templates:', error);
      }
    },
    sendBroadcast() {
      this.$v.$touch();
      if (this.$v.$invalid) return;
      if (!this.inboxSelected) {
        this.showAlert(this.$t('BROADCAST.SELECT_INBOX_ALERT'));
        return;
      }
      if (this.selectedCustomer.length == 0) {
        this.showAlert(this.$t('BROADCAST.SELECT_CUSTOMER_ALERT'));
        return;
      }
      if (!this.selectedTemplateValue) {
        this.showAlert(this.$t('BROADCAST.SELECT_TEMPLATE_ALERT'));
        return;
      }
      this.showConfirmModal = true;
    },
    closeConfirm() {
      this.showConfirmModal = false;
    },
    closeModal() {
      this.showCustomerModal = false;
      this.meta.currentPage = 1;
    },
    getRowStyleOption() {
      return {
        clickHighlight: false,
        stripe: false,
      };
    },
    showCustomerList() {
      this.checkboxOption.selectedRowKeys = this.selectedCustomer;
      this.showCustomerModal = true;
      this.fetchPackageList();
    },
    goBack() {
      this.$router.push(
        `/app/accounts/${this.$route.params.accountId}/broadcasts`
      );
    },
    onInboxSelected() {
      this.selectedTemplate = '';
      this.selectedTemplateValue = '';
      this.processedParams = {};
      if (this.inboxSelected.value) {
        this.fetchTemplates();
      }
    },
    onTemplateChange() {
      this.processedParams = {};
      const selectedTemplate = this.templates.find(
        template => template.id === this.selectedTemplateValue.value
      );
      const paymentKeywords = ['tagihan'];

      const isPayment = paymentKeywords.some(keyword =>
        selectedTemplate.name.toLowerCase().includes(keyword)
      );

      if (isPayment) {
        this.isPaymentTemplate = isPayment;
      }
      this.selectedTemplate = selectedTemplate;
      this.templateHeaderUrl = null;
      this.templateButtonUrl = '';

      this.generateVariables();
    },
    onSearchPelanggan() {
      this.fetchCustomers();
    },
    onPackageChange() {
      this.meta.currentPage = 1;
      this.fetchCustomers();
    },
    onRemovePackage() {
      this.meta.currentPage = 1;
      this.fetchCustomers();
    },
    onHdrChange() {
      this.meta.currentPage = 1;
      this.fetchCustomers();
    },
    onRemoveHdr() {
      this.meta.currentPage = 1;
      this.fetchCustomers();
    },
    async fetchCustomers() {
      try {
        this.$store.dispatch('orders/getPelanggan', {
          page: this.meta.currentPage,
          per_page: this.meta.perPage,
          order_by: '',
          order: '',
          search: this.searchQuery,
          filter: 'all',
          status: this.selectedStatus.value || 'all',
          select_nama_paket: this.selectedPackages.map(item => item.value),
          select_hdr: this.selectedHdr.map(item => item.value),
        });
      } catch (error) {
        this.showAlert(this.$t('BROADCAST.ERROR_FETCHING_CUSTOMER'));

        console.error('Error fetching customers', error);
      }
    },
    processVariable(str) {
      return str.replace(/{{|}}/g, '');
    },
    generateVariables() {
      const matchedVariables = this.templateString.match(/{{([^}]+)}}/g);
      if (!matchedVariables) return;

      const variables = matchedVariables.map(i => this.processVariable(i));
      this.processedParams = variables.reduce((acc, variable) => {
        acc[variable] = '';
        return acc;
      }, {});
    },
    openVariableTypeSelector(key) {
      this.selectedParamKey = key;
      this.showTypeModal = true;
    },
    closeVariableTypeModal() {
      this.showTypeModal = false;
      this.selectedParamKey = null;
    },
    closeButtonTypeModal() {
      this.showButtonTypeModal = false;
    },
    selectVariableType(type) {
      if (!this.selectedParamKey) return;

      this.parameterTypes[this.selectedParamKey] = type;

      const paramType = this.paramsTypeOptions.find((paramType) => paramType.value == type)

      if(paramType.type == 'backend') {
        this.processedParams[this.selectedParamKey] = type;
      } else {
        this.processedParams[this.selectedParamKey] = '';
      }

      this.closeVariableTypeModal();
    },
    selectButtonType(type) {
      this.closeButtonTypeModal()
      if (type == 'manual') {
        return this.templateButtonUrl = '';
      }

      return (this.templateButtonUrl = type);
    },
    async fetchPackageList() {
      try {
        this.$store.dispatch('orders/getPaketLangganan', {
          per_page: 100,
        });
      } catch (error) {
        this.showAlert(this.$t('BROADCAST.ERROR_FETCHING_PACKAGE'));

        console.error('Error while fetching package data:', error);
      }
    },
  },
  mounted() {
    this.$store.dispatch('hdr/get');
    this.$store.dispatch('broadcastsView/getInbox');
    this.fetchCustomers();
  },
};
</script>

<style scoped>
.conversation-page {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 0;
  margin: 0;
  width: 100%;
  height: 100vh;
}
.whatsapp-container {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica,
    Arial, sans-serif;
  max-width: 100%;
  padding: 8px 12px;
  border-radius: 7.5px;
  background-color: #e5ddd5;
  background-image: url('data:image/png;base64,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');
}

.whatsapp-message {
  position: relative;
  max-width: 90%;
  margin-right: 12px;
  margin-bottom: 15px;
  margin-top: 15px;
}

.message-content {
  background-color: #dcf8c6;
  padding: 8px 10px;
  border-radius: 7.5px;
  box-shadow: 0 1px 0.5px rgba(0, 0, 0, 0.13);
  word-wrap: break-word;
}

.info-text {
  font-size: 12px;
  color: black;
  margin-top: 12px;
  margin-bottom: 12px;
}

.button-container {
  display: flex;
  justify-content: center;
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.payment-button {
  font-size: 13px;
  color: #0000ee;
  background: transparent;
  border: none;
  font-weight: 500;
  padding: 4px 16px;
  cursor: pointer;
}

.message-tail {
  position: absolute;
  bottom: 3px;
  right: -8px;
  width: 16px;
  height: 10px;
  background-color: #dcf8c6;
  clip-path: polygon(0 0, 100% 100%, 0 100%);
}

.custom-multiselect {
  max-width: 100%;
  white-space: normal;
}
.broadcast-table {
  margin: 0;
  padding: 0;
  width: 100%;
  flex-grow: 1;
  overflow: auto;
}
.red-text {
  color: red;
}

.template-block {
  @apply items-start flex flex-col my-0;
}

.customer-package {
  @apply items-start flex flex-col mt-2 text-xs text-slate-300;
}

.woz.list-group-itemz:first-child {
  border-top-left-radius: inherit;
  border-top-right-radius: inherit;
}

.wo.list-group-item {
  border: 1px solid #697076;
}

.wo.list-group-item:hover {
  background-color: #6e6e6e;
  color: #ffffff;
}

.error {
  @apply bg-red-100 dark:bg-red-100 rounded-md text-red-800 dark:text-red-800 p-2.5 text-center;
}

.variable-input {
  padding: 10px;
}

.multiselect-wrap--medium .mx-input {
  padding: 0.5rem 0.75rem; /* contoh: py-2 px-3 */
  height: 46px; /* jika mau tinggi sama */
  border-radius: 0.375rem; /* jika ingin sudutnya rounded */
  border: 1px solid #d1d5db; /* warna border abu-abu (#d1d5db ~ Tailwind gray-300) */
  font-size: 1rem;
}

.search-wrap .contact-search {
  @apply pl-9 pr-[3.75rem] text-sm w-[50%] justify-center h-[2.375rem] m-0;
}

.empty-data {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  width: 100%;
  color: #ffffff;
  font-size: 16px;
}
</style>
